import React, { useState } from "react";

interface ChessBoardViewerProps {
  fen?: string;
  moves?: readonly string[];
  orientation?: "white" | "black";
  interactive?: boolean;
  onMoveSelect?: (moveIndex: number) => void;
  className?: string;
}

const PIECE_SYMBOLS: Record<string, string> = {
  P: "♙",
  N: "♘",
  B: "♗",
  R: "♖",
  Q: "♕",
  K: "♔",
  p: "♟",
  n: "♞",
  b: "♝",
  r: "♜",
  q: "♛",
  k: "♚",
};

const PIECE_NAMES: Record<string, string> = {
  P: "White Pawn",
  N: "White Knight",
  B: "White Bishop",
  R: "White Rook",
  Q: "White Queen",
  K: "White King",
  p: "Black Pawn",
  n: "Black Knight",
  b: "Black Bishop",
  r: "Black Rook",
  q: "Black Queen",
  k: "Black King",
};

function parseFenBoard(fen: string): (string | null)[][] {
  const boardPart =
    fen.split(" ")[0] || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR";
  const rows = boardPart.split("/");
  const board: (string | null)[][] = [];

  for (const row of rows) {
    const rowPieces: (string | null)[] = [];
    for (const char of row) {
      if (char >= "1" && char <= "8") {
        const empties = parseInt(char, 10);
        for (let i = 0; i < empties; i++) {
          rowPieces.push(null);
        }
      } else {
        rowPieces.push(char);
      }
    }
    board.push(rowPieces);
  }
  return board;
}

export function ChessBoardViewer({
  fen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
  moves = [],
  orientation = "white",
  onMoveSelect,
  className = "",
}: ChessBoardViewerProps): React.JSX.Element {
  const [boardOrientation, setBoardOrientation] = useState(orientation);
  const [selectedMove, setSelectedMove] = useState(moves.length);
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null);

  const rawBoard = parseFenBoard(fen);
  const board =
    boardOrientation === "white"
      ? rawBoard
      : [...rawBoard].reverse().map((row) => [...row].reverse());

  const files =
    boardOrientation === "white"
      ? ["a", "b", "c", "d", "e", "f", "g", "h"]
      : ["h", "g", "f", "e", "d", "c", "b", "a"];
  const ranks =
    boardOrientation === "white"
      ? ["8", "7", "6", "5", "4", "3", "2", "1"]
      : ["1", "2", "3", "4", "5", "6", "7", "8"];

  function handleSquareClick(square: string) {
    if (selectedSquare === square) {
      setSelectedSquare(null);
    } else {
      setSelectedSquare(square);
    }
  }

  function handlePrev() {
    if (selectedMove > 0) {
      const next = selectedMove - 1;
      setSelectedMove(next);
      onMoveSelect?.(next);
    }
  }

  function handleNext() {
    if (selectedMove < moves.length) {
      const next = selectedMove + 1;
      setSelectedMove(next);
      onMoveSelect?.(next);
    }
  }

  function toggleOrientation() {
    setBoardOrientation((prev) => (prev === "white" ? "black" : "white"));
  }

  return (
    <div
      className={`chess-viewer-container ${className}`}
      role="region"
      aria-label="Interactive Chessboard"
    >
      <div className="chess-board-header">
        <div className="board-orientation-badge">
          Viewing as:{" "}
          <strong>{boardOrientation === "white" ? "White" : "Black"}</strong>
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={toggleOrientation}
          aria-label="Flip board orientation"
        >
          Flip Board
        </button>
      </div>

      <div className="board-wrapper">
        <div
          className="board-grid"
          role="grid"
          aria-label="Chessboard 8 by 8 squares"
        >
          {board.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="board-row" role="row">
              {row.map((piece, colIndex) => {
                const squareName = `${files[colIndex]}${ranks[rowIndex]}`;
                const isLight = (rowIndex + colIndex) % 2 === 0;
                const isSelected = selectedSquare === squareName;
                const pieceLabel = piece ? PIECE_NAMES[piece] : "Empty";

                return (
                  <button
                    key={squareName}
                    type="button"
                    role="gridcell"
                    className={`board-square ${isLight ? "light-sq" : "dark-sq"} ${isSelected ? "selected-sq" : ""}`}
                    onClick={() => handleSquareClick(squareName)}
                    aria-label={`${squareName}: ${pieceLabel}`}
                  >
                    {colIndex === 0 && (
                      <span className="sq-coord-rank">{ranks[rowIndex]}</span>
                    )}
                    {rowIndex === 7 && (
                      <span className="sq-coord-file">{files[colIndex]}</span>
                    )}
                    {piece && (
                      <span
                        className={`chess-piece ${piece === piece.toUpperCase() ? "piece-white" : "piece-black"}`}
                        aria-hidden="true"
                      >
                        {PIECE_SYMBOLS[piece] || piece}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {moves.length > 0 && (
        <div className="board-move-controls" aria-label="Move navigation">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              setSelectedMove(0);
              onMoveSelect?.(0);
            }}
            disabled={selectedMove === 0}
            aria-label="First move"
          >
            « First
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={handlePrev}
            disabled={selectedMove === 0}
            aria-label="Previous move"
          >
            ‹ Prev
          </button>
          <span className="move-step-indicator">
            Move {selectedMove} of {moves.length}
          </span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={handleNext}
            disabled={selectedMove === moves.length}
            aria-label="Next move"
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              setSelectedMove(moves.length);
              onMoveSelect?.(moves.length);
            }}
            disabled={selectedMove === moves.length}
            aria-label="Latest move"
          >
            Latest »
          </button>
        </div>
      )}
    </div>
  );
}
